import React, { useMemo } from 'react';
import { useSwipeable } from 'react-swipeable';
import { Portal } from '@headlessui/react';

import { ImageFitMode } from '../../utils/imagekit';
import { Image } from '../elements/image';
import ChevronLeftIcon from '../../icons/chevron-left';
import ChevronRightIcon from '../../icons/chevron-right';

interface ProductImageProps {
  url: string;
  name: string;
}

const ProductImage: React.FC<ProductImageProps> = (props) => {
  let { url, name } = props;

  return (
    <Image
      url={url}
      width={600}
      fitMode={ImageFitMode.Crop}
      alt={name}
      sizes="(max-width: 350px) 300px, 600px"
      ar={{
        width: 0.75,
        height: 1,
      }}
      className="w-full"
    />
  );
};

interface Props {
  images: Array<string>;
  name: string;
  className?: string;
}

const ProductImages: React.FC<Props> = (props) => {
  let { images = [], name } = props;
  let [selectedIndexState, setSelectedIndexState] = React.useState(0);

  const { selectedIndex, selectedImage } = useMemo(() => {
    let normalizedSelectedIndex = selectedIndexState >= images.length ? images.length - 1 : selectedIndexState;

    return {
      selectedIndex: normalizedSelectedIndex,
      selectedImage: images[normalizedSelectedIndex] || '#',
    };
  }, [images, selectedIndexState]);

  const handleNext = () => {
    setSelectedIndexState((selectedIndex + 1) % images.length);
  };

  const handlePrevious = () => {
    setSelectedIndexState((selectedIndex - 1 + images.length) % images.length);
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handlePrevious(),
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  return (
    <>
      <Portal>
        <div className="hidden">
          {images.map((image, i) => {
            return <ProductImage url={image} name={name} key={`${image}-${i}`} />;
          })}
        </div>
      </Portal>

      <div {...handlers} className="w-full relative">
        <button
          className="absolute left-0 bg-isabelline border-2 border-raw-umber text-raw-umber hover:bg-raw-umber hover:text-white p-2"
          style={{ top: '50%', marginTop: '-1rem' }}
          onClick={handlePrevious}
        >
          <ChevronLeftIcon className="w-6 h-6" />
        </button>
        <ProductImage url={selectedImage} name={name} />
        <button
          className="absolute right-0 bg-isabelline border-2 border-raw-umber text-raw-umber hover:bg-raw-umber hover:text-white p-2"
          style={{ top: '50%', marginTop: '-1rem' }}
          onClick={handleNext}
        >
          <ChevronRightIcon className="w-6 h-6" />
        </button>
        <div className="bg-isabelline text-raw-umber py-2 px-4 absolute right-0 bottom-0">
          {selectedIndex + 1} / {images.length}
        </div>
      </div>
    </>
  );
};

export default ProductImages;
